import { Button, OptionList, Popover } from '@shopify/polaris';
import { useCallback, useMemo, useState } from 'react';
import { RETAIL_DEPOSIT_TYPES } from '../../../helpers/depositTypes';

/**
 * Selector del alcance de depósitos de los informes (eficacia, reposición).
 * Permite combinar tres dimensiones, acumulativas (unión):
 *   - Roles (`depositType`): "todas las tiendas", "el canal online"…
 *   - Grupos (`isOnlyGroup`): el back los expande a sus hojas operables.
 *   - Depósitos concretos.
 * Más el sentinel ALL (todos incluido central), que es excluyente con el
 * resto: marcarlo desmarca lo demás y viceversa.
 *
 * **Default visible**: sin selección del usuario, el back aplica el
 * perímetro de venta, y el popover lo enseña con los roles de venta ya
 * marcados. Ese pre-marcado es solo visual: el estado sigue vacío (URL
 * limpia, botón sin resaltar, sin chips) hasta que el usuario toca algo.
 * Y a la inversa: si el usuario deja marcado exactamente el default, la
 * selección se des-materializa y vuelve al estado vacío.
 *
 * Los valores internos del OptionList van prefijados (`r:`, `g:`, `d:`)
 * para poder mezclar las tres secciones en un único `selected`.
 */
const DepositScopeFilter = ({
  title,
  allSelected,
  roles,
  groups,
  deposits,
  roleOptions,
  groupOptions,
  depositOptions,
  onChange,
  // Roles que el back aplica cuando no hay selección. Eficacia y reposición
  // usan el perímetro de venta; existencias pasa `[]` (sin selección =
  // todos los depósitos operables, nada pre-marcado).
  defaultRoles = RETAIL_DEPOSIT_TYPES,
}) => {
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopover = useCallback(() => setPopoverActive((v) => !v), []);

  const isDefaultState = !allSelected && !roles.length && !groups.length && !deposits.length;

  const selected = useMemo(() => {
    if (isDefaultState) return defaultRoles.map((r) => `r:${r}`);
    return [
      ...(allSelected ? ['ALL'] : []),
      ...roles.map((r) => `r:${r}`),
      ...groups.map((g) => `g:${g}`),
      ...deposits.map((d) => `d:${d}`),
    ];
  }, [isDefaultState, defaultRoles, allSelected, roles, groups, deposits]);

  const sections = useMemo(
    () => [
      { options: [{ value: 'ALL', label: 'Todos los depósitos (incl. central)' }] },
      { title: 'Por rol', options: roleOptions.map((o) => ({ value: `r:${o.value}`, label: o.label })) },
      { title: 'Por grupo', options: groupOptions.map((o) => ({ value: `g:${o.value}`, label: o.label })) },
      { title: 'Por depósito', options: depositOptions.map((o) => ({ value: `d:${o.value}`, label: o.label })) },
    ],
    [roleOptions, groupOptions, depositOptions],
  );

  const handleChange = useCallback(
    (next) => {
      // ALL es excluyente: si acaba de marcarse, gana; si había ALL y se
      // marca otra cosa, ALL se suelta.
      const nowAll = next.includes('ALL');
      if (nowAll && !allSelected) {
        onChange({ all: true, roles: [], groups: [], deposits: [] });
        return;
      }
      const rest = next.filter((v) => v !== 'ALL');
      const parsed = {
        all: false,
        roles: rest.filter((v) => v.startsWith('r:')).map((v) => v.slice(2)),
        groups: rest.filter((v) => v.startsWith('g:')).map((v) => v.slice(2)),
        deposits: rest.filter((v) => v.startsWith('d:')).map((v) => v.slice(2)),
      };

      // Si lo marcado coincide exactamente con el default (los roles de
      // venta, nada más), se emite estado vacío: el default no es un filtro
      // del usuario y no debe materializarse en la URL ni en los chips.
      const matchesDefault =
        !parsed.groups.length &&
        !parsed.deposits.length &&
        parsed.roles.length === defaultRoles.length &&
        defaultRoles.every((r) => parsed.roles.includes(r));

      onChange(matchesDefault ? { all: false, roles: [], groups: [], deposits: [] } : parsed);
    },
    [allSelected, defaultRoles, onChange],
  );

  // El botón solo se resalta con selección REAL del usuario: el pre-marcado
  // del default es informativo y no debe parecer un filtro activo.
  const activator = (
    <Button onClick={togglePopover} disclosure primary={!isDefaultState}>
      {title}
    </Button>
  );

  return (
    <Popover active={popoverActive} activator={activator} onClose={togglePopover}>
      <Popover.Pane>
        <OptionList onChange={handleChange} sections={sections} selected={selected} allowMultiple />
      </Popover.Pane>
    </Popover>
  );
};

export default DepositScopeFilter;
